import { Outlet, Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { useState, useEffect } from 'react';
import { Logo } from '@/components/ui/Logo';
import { FaWhatsapp } from 'react-icons/fa';
import { useSanityContext } from '@/sanity/SanityDataProvider';

// R2 - Navigation structure as const for type safety
const NAV_ITEMS = [
  { label: 'Inicio', href: '/' },
  { label: 'Quiénes Somos', href: '/quienes-somos' },
  { label: 'Productos', href: '/productos' },
  { label: 'Contacto', href: '/contacto' },
] as const;

// WhatsApp contact link
const WHATSAPP_URL = 'https://wa.me/?text=Hola%20INBOCA%2C%20me%20gustaría%20consultar%20sobre%20sus%20productos';

// R2 - Helper to check if a nav item is active
function isActiveRoute(href: string, pathname: string): boolean {
  if (href === '/') {
    return pathname === '/';
  }
  return pathname.startsWith(href);
}

/**
 * R2 - Premium INBOCA layout with glassmorphism navbar
 * R4 - Includes shimmer border animation, active nav state, and hover effects
 */
export function MainLayout() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { pathname } = useLocation();
  const { data } = useSanityContext();

  // R4 - Resolve WhatsApp URL from Sanity contactInfo or use fallback
  const ci = data?.contactInfo;
  const resolvedWhatsappUrl = ci
    ? `https://wa.me/${ci.whatsappNumber}?text=${encodeURIComponent(ci.whatsappDefaultMessage)}`
    : WHATSAPP_URL;

  // R4 - Scroll to top when route changes
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return (
    <div className="min-h-screen bg-white">
      {/* Navbar - Glassmorphism with shimmer border */}
      <nav className="sticky top-0 z-50 bg-white/80 backdrop-blur-md shadow-sm animate-fade-in">
        {/* Shimmer border - animated gradient line */}
        <div
          className="absolute bottom-0 left-0 right-0 h-[2px] animate-shimmer"
          style={{
            background: 'linear-gradient(90deg, transparent, #0097a7, #f57c00, #0097a7, transparent)',
            backgroundSize: '200% 100%',
          }}
        />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            {/* Logo - Increased size for prominence */}
            <Link 
              to="/" 
              className="flex items-center gap-2 group py-1" 
              aria-label="INBOCA Home"
            >
              {/* Desktop: Full logo - larger size */}
              <Logo
                variant="full"
                bg="transparent"
                priority="eager"
                className="h-10 lg:h-12 hidden sm:block transition-all duration-300 group-hover:scale-105"
              />
              {/* Mobile: Icon only */}
              <Logo
                variant="icon"
                bg="transparent"
                priority="eager"
                className="h-10 sm:hidden transition-all duration-300 group-hover:scale-105"
              />
            </Link>

            {/* Desktop Navigation - Active state + hover effects */}
            <div className="hidden md:flex items-center gap-2">
              {NAV_ITEMS.map((item) => {
                const isActive = isActiveRoute(item.href, pathname);
                return (
                  <Link
                    key={item.label}
                    to={item.href}
                    className={`relative font-semibold px-4 py-2 rounded-lg transition-all duration-300 group ${
                      isActive
                        ? 'text-inboca-teal bg-inboca-teal/10'
                        : 'text-gray-700 hover:text-inboca-teal hover:bg-inboca-teal/10'
                    }`}
                  >
                    {item.label}
                    {/* Underline - visible when active, expands on hover */}
                    <span
                      className={`absolute bottom-1 left-1/2 -translate-x-1/2 h-0.5 bg-inboca-teal rounded-full transition-all duration-300 ${
                        isActive ? 'w-2/3' : 'w-0 group-hover:w-2/3'
                      }`}
                    />
                  </Link>
                );
              })}
            </div>

            {/* CTA Button & Mobile Menu */}
            <div className="flex items-center gap-4">
              {/* CTA Button - Premium style */}
              <a
                href={resolvedWhatsappUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="hidden sm:inline-flex items-center gap-2 bg-inboca-orange text-white px-5 py-2.5 rounded-lg font-semibold shadow-lg transition-all duration-300 hover:bg-inboca-orange/90 hover:shadow-xl hover:scale-105"
              >
                <FaWhatsapp className="h-5 w-5" />
                <span>Hablemos</span>
              </a>

              {/* Mobile Menu Button */}
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="md:hidden p-2 text-gray-700 hover:text-inboca-teal hover:bg-inboca-teal/10 rounded-lg transition-all duration-300"
                aria-label="Toggle menu"
              >
                {mobileMenuOpen ? (
                  <X className="h-6 w-6" />
                ) : (
                  <Menu className="h-6 w-6" />
                )}
              </button>
            </div>
          </div>

          {/* Mobile Navigation - Glassmorphism with active states */}
          {mobileMenuOpen && (
            <div className="md:hidden border-t border-gray-200/50 py-4 bg-white/90 backdrop-blur-md animate-fade-in rounded-b-xl">
              <div className="flex flex-col gap-2 px-2">
                {NAV_ITEMS.map((item) => {
                  const isActive = isActiveRoute(item.href, pathname);
                  return (
                    <Link
                      key={item.label}
                      to={item.href}
                      className={`font-semibold px-4 py-3 rounded-lg transition-all duration-300 ${
                        isActive
                          ? 'text-inboca-teal bg-inboca-teal/10 border-l-4 border-inboca-teal'
                          : 'text-gray-700 hover:text-inboca-teal hover:bg-inboca-teal/10'
                      }`}
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      {item.label}
                    </Link>
                  );
                })}
                {/* Mobile CTA */}
                <a
                  href={resolvedWhatsappUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 bg-inboca-orange text-white px-4 py-3 rounded-lg font-semibold mt-2 shadow-lg transition-all duration-300 hover:bg-inboca-orange/90"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <FaWhatsapp className="h-5 w-5" />
                  <span>Hablemos</span>
                </a>
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* Main Content */}
      <main>
        <Outlet />
      </main>
    </div>
  );
}
